import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import Button from "../components/common/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/common/Select";
import { Checkbox } from "../components/common/Checkbox";
import { Badge } from "../components/common/Badge";
import { Calendar } from "../components/common/Calendar";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import {
  SCHEDULE_TYPE,
  CONSULTATION_MODE,
  getScheduleConsultationMode,
  formatClockTimeRange,
  formatDate,
  formatDateDMY,
  formatScheduleSpecificDates,
  getScheduleType,
  getScheduleTypeLabel,
} from "../lib/ScheduleUtils";
import {
  getLocalizedDayName,
  getLocalizedAppointmentType,
  getLocalizedScheduleType,
  WEEK_DAY_INDICES,
} from "../i18n/scheduleLabels";
import { Trash2, Plus, ArrowLeft, Info, Clock, CalendarDays } from "lucide-react";
import customToast from "./common/Toaster";
import { cn } from "../utils/utils";
import { ConsultationModeBadge } from "./ConsultationMode";
import useBrandTranslation from "../hooks/useBrandTranslation";

const SCHEDULE_TYPE_OPTIONS = [
  { value: SCHEDULE_TYPE.HABITUAL, labelKey: "habitual" },
  { value: SCHEDULE_TYPE.OCCASIONAL, labelKey: "occasional" },
  { value: SCHEDULE_TYPE.BLOCKED, labelKey: "blocked" },
];

/** En Horarios Configurados: Habitual usa el azul de Obra Social (invertido). */
function scheduleTypeBadgeVariant(type) {
  if (type === SCHEDULE_TYPE.OCCASIONAL) return "scheduleOccasional";
  if (type === SCHEDULE_TYPE.BLOCKED) return "scheduleBlocked";
  return "appointmentInsurance";
}

const SCHEDULE_TYPE_BUTTON_SELECTED = {
  [SCHEDULE_TYPE.HABITUAL]:
    "border-blue-600 bg-blue-50 text-blue-900",
  [SCHEDULE_TYPE.OCCASIONAL]:
    "border-amber-500 bg-amber-50 text-amber-900",
  [SCHEDULE_TYPE.BLOCKED]:
    "border-red-500 bg-red-50 text-red-900",
};

export default function DoctorScheduleConfig() {
  const { t, language } = useBrandTranslation();
  const { user } = useAuth();
  const { addSchedule, deleteSchedule, getSchedulesByDoctor } = useData();
  const navigate = useNavigate();

  const [scheduleType, setScheduleType] = useState(SCHEDULE_TYPE.HABITUAL);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [blockReason, setBlockReason] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [appointmentType, setAppointmentType] = useState("private");
  const [slotDuration, setSlotDuration] = useState(30);
  const [consultationMode, setConsultationMode] = useState(
    CONSULTATION_MODE.IN_PERSON
  );

  if (!user || user.role !== "doctor") return null;

  const doctorSchedules = getSchedulesByDoctor(user.id);

  const schedulesGroupedByDay = useMemo(() => {
    const habitual = doctorSchedules.filter(
      (s) => getScheduleType(s) === SCHEDULE_TYPE.HABITUAL
    );
    const byDay = {};
    habitual.forEach((schedule) => {
      const day = Number(schedule.day_of_week);
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(schedule);
    });
    return Object.entries(byDay)
      .map(([day, items]) => ({
        day: Number(day),
        dayName: getLocalizedDayName(day),
        items: items.sort((a, b) =>
          (a.start_time || "").localeCompare(b.start_time || "")
        ),
      }))
      .sort((a, b) => a.day - b.day);
  }, [doctorSchedules, language]);

  const occasionalSchedules = useMemo(
    () =>
      doctorSchedules
        .filter((s) => getScheduleType(s) === SCHEDULE_TYPE.OCCASIONAL)
        .sort((a, b) =>
          (a.start_time || "").localeCompare(b.start_time || "")
        ),
    [doctorSchedules]
  );

  const blockedSchedules = useMemo(
    () =>
      doctorSchedules
        .filter((s) => getScheduleType(s) === SCHEDULE_TYPE.BLOCKED)
        .sort((a, b) =>
          formatScheduleSpecificDates(a).localeCompare(
            formatScheduleSpecificDates(b)
          )
        ),
    [doctorSchedules]
  );

  const handleDayToggle = (dayIndex) => {
    setSelectedDays((prev) =>
      prev.includes(dayIndex)
        ? prev.filter((d) => d !== dayIndex)
        : [...prev, dayIndex]
    );
  };

  const resetFormPartial = () => {
    setSelectedDays([]);
    setSelectedDates([]);
    setBlockReason("");
  };

  const handleAddSchedule = async () => {
    if (scheduleType === SCHEDULE_TYPE.HABITUAL) {
      if (selectedDays.length === 0) {
        customToast(t("app.scheduleConfig.selectDay", "Selecciona al menos un día"), "warning");
        return;
      }
      if (startTime >= endTime) {
        customToast(
          t("app.scheduleConfig.startBeforeEnd", "La hora de inicio debe ser menor que la hora de fin"),
          "warning"
        );
        return;
      }

      let allSuccessful = true;
      const errors = [];

      for (const dayOfWeek of selectedDays) {
        const result = await addSchedule({
          doctorId: user.id,
          scheduleType: SCHEDULE_TYPE.HABITUAL,
          dayOfWeek,
          startTime,
          endTime,
          appointmentType,
          consultationMode,
          slotDuration,
          isActive: true,
        });

        if (!result.success) {
          allSuccessful = false;
          errors.push(`${getLocalizedDayName(dayOfWeek)}: ${result.error}`);
        }
      }

      if (allSuccessful) {
        resetFormPartial();
        customToast(t("app.scheduleConfig.addedSuccess", "Horarios agregados exitosamente"), "success");
      } else {
        customToast(
          t("app.scheduleConfig.addSomeSchedulesError", "Error al agregar algunos horarios:\n\n{{errors}}", {
            errors: errors.join("\n"),
          }),
          "danger"
        );
      }
      return;
    }

    if (selectedDates.length === 0) {
      customToast(t("app.scheduleConfig.selectDate", "Selecciona al menos una fecha en el calendario"), "warning");
      return;
    }

    const specificDates = selectedDates.map(formatDate).sort();

    if (scheduleType === SCHEDULE_TYPE.BLOCKED) {
      if (!blockReason.trim()) {
        customToast(
          t(
            "app.scheduleConfig.blockReasonRequired",
            "Indica el motivo del bloqueo (ej. Feriado, Vacaciones)"
          ),
          "warning"
        );
        return;
      }

      const result = await addSchedule({
        doctorId: user.id,
        scheduleType: SCHEDULE_TYPE.BLOCKED,
        specificDates,
        blockReason: blockReason.trim(),
        startTime: "00:00",
        endTime: "23:59",
        appointmentType: "private",
        slotDuration: 30,
        isActive: true,
      });

      if (result.success) {
        resetFormPartial();
        customToast(t("app.scheduleConfig.blockedSuccess", "Días bloqueados correctamente"), "success");
      } else {
        customToast(result.error || t("app.scheduleConfig.blockError", "Error al bloquear fechas"), "danger");
      }
      return;
    }

    if (startTime >= endTime) {
      customToast(
        t("app.scheduleConfig.startBeforeEnd", "La hora de inicio debe ser menor que la hora de fin"),
        "warning"
      );
      return;
    }

    const result = await addSchedule({
      doctorId: user.id,
      scheduleType: SCHEDULE_TYPE.OCCASIONAL,
      specificDates,
      startTime,
      endTime,
      appointmentType,
      consultationMode,
      slotDuration,
      isActive: true,
    });

    if (result.success) {
      resetFormPartial();
      customToast(t("app.scheduleConfig.occasionalAdded", "Horario ocasional agregado"), "success");
    } else {
      customToast(result.error || t("app.scheduleConfig.occasionalError", "Error al agregar horario ocasional"), "danger");
    }
  };

  const renderScheduleTypeBadge = (slot) => (
    <Badge
      variant={scheduleTypeBadgeVariant(getScheduleType(slot))}
      className="text-xs"
    >
      {getScheduleTypeLabel(slot)}
    </Badge>
  );

  const renderAppointmentBadge = (slot) => {
    const typeName = getLocalizedAppointmentType(slot.appointment_type);
    return (
      <Badge
        variant={
          slot.appointment_type === "private"
            ? "appointmentPrivate"
            : "scheduleHabitual"
        }
        className="text-xs"
      >
        {typeName}
      </Badge>
    );
  };

  const deleteButton = (slot) => (
    <Button
      variant="ghost"
      size="sm"
      className="shrink-0 text-gray-500 hover:text-red-600"
      onClick={() => {
        if (
          confirm(t("app.scheduleConfig.confirmDelete", "¿Confirmas que deseas eliminar el horario seleccionado?"))
        ) {
          deleteSchedule(slot.id);
        }
      }}
    >
      <Trash2 className="w-4 h-4" />
    </Button>
  );

  const hasAnySchedules =
    schedulesGroupedByDay.length > 0 ||
    occasionalSchedules.length > 0 ||
    blockedSchedules.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => navigate("/turnos")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t("app.scheduleConfig.pageTitle", "Configuración de Horarios")}</h1>
          <p className="text-gray-600">
            {t("app.scheduleConfig.pageSubtitle", "Define tus horarios de atención, fechas especiales y días sin turnos")}
          </p>
        </div>

        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-blue-900 text-sm">
              {t(
                "app.scheduleConfig.shareSecretaryHint",
                "No olvides compartir tus horarios con tu secretaria, para que pueda gestionar tu agenda."
              )}{" "}
              <button
                onClick={() => navigate("/doctor/share")}
                className="text-blue-600 hover:text-blue-800 underline font-medium"
              >
                {t("app.scheduleConfig.shareSchedulesLink", "Compartir horarios")}
              </button>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("app.scheduleConfig.addTitle", "Agregar Nuevo Horario")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-base font-medium mb-2 block">
                  {t("app.scheduleConfig.scheduleTypeLabel", "Tipo de horario")}
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {SCHEDULE_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setScheduleType(opt.value);
                        resetFormPartial();
                      }}
                      className={cn(
                        "rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
                        scheduleType === opt.value
                          ? SCHEDULE_TYPE_BUTTON_SELECTED[opt.value]
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      )}
                    >
                      {getLocalizedScheduleType(opt.value)}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {scheduleType === SCHEDULE_TYPE.HABITUAL &&
                    t(
                      "app.scheduleConfig.typeHabitualHint",
                      "Horario recurrente por día de la semana (como hasta ahora)."
                    )}
                  {scheduleType === SCHEDULE_TYPE.OCCASIONAL &&
                    t(
                      "app.scheduleConfig.typeOccasionalHint",
                      "Atención solo en fechas puntuales del año que elijas."
                    )}
                  {scheduleType === SCHEDULE_TYPE.BLOCKED &&
                    t(
                      "app.scheduleConfig.typeBlockedHint",
                      "Sin turnos en esas fechas (feriados, vacaciones, congresos, etc.)."
                    )}
                </p>
              </div>

              {scheduleType === SCHEDULE_TYPE.HABITUAL && (
                <div>
                  <Label className="text-base font-medium">
                    {t("app.scheduleConfig.daysOfWeekLabel", "Días de la semana")}
                  </Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {WEEK_DAY_INDICES.map((index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Checkbox
                          id={`day-${index}`}
                          checked={selectedDays.includes(index)}
                          onCheckedChange={() => handleDayToggle(index)}
                        />
                        <Label htmlFor={`day-${index}`} className="text-sm">
                          {getLocalizedDayName(index)}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(scheduleType === SCHEDULE_TYPE.OCCASIONAL ||
                scheduleType === SCHEDULE_TYPE.BLOCKED) && (
                <div>
                  <Label className="text-base font-medium flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />
                    Fechas seleccionadas
                  </Label>
                  <Calendar
                    mode="multiple"
                    selected={selectedDates}
                    onSelect={(dates) => setSelectedDates(dates || [])}
                    className="rounded-md border mt-2"
                  />
                  {selectedDates.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      {selectedDates.length === 1
                        ? t("app.scheduleConfig.dateCount", "{{count}} fecha", {
                            count: selectedDates.length,
                          })
                        : t("app.scheduleConfig.datesCount", "{{count}} fechas", {
                            count: selectedDates.length,
                          })}
                      :{" "}
                      {selectedDates
                        .map((d) => formatDateDMY(d))
                        .sort()
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}

              {scheduleType === SCHEDULE_TYPE.BLOCKED && (
                <div>
                  <Label htmlFor="blockReason">{t("app.scheduleConfig.blockReason", "Motivo del bloqueo")}</Label>
                  <Input
                    id="blockReason"
                    placeholder={t("app.scheduleConfig.blockReasonPlaceholder", "Ej. Feriado, Vacaciones, Congreso...")}
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                  />
                </div>
              )}

              {scheduleType !== SCHEDULE_TYPE.BLOCKED && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="startTime">{t("app.scheduleConfig.startTime", "Hora de inicio")}</Label>
                      <Input
                        id="startTime"
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="endTime">{t("app.scheduleConfig.endTime", "Hora de fin")}</Label>
                      <Input
                        id="endTime"
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="appointmentType">{t("app.scheduleConfig.appointmentType", "Tipo de consulta")}</Label>
                    <Select
                      value={appointmentType}
                      onValueChange={(value) => setAppointmentType(value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">{t("schedule.appointmentTypes.private", "Particular")}</SelectItem>
                        <SelectItem value="insurance">{t("schedule.appointmentTypes.insurance", "Obra Social")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="consultationMode">
                      {t("schedule.consultationModeLabel", "Formato de consulta")}
                    </Label>
                    <Select
                      value={consultationMode}
                      onValueChange={(value) => setConsultationMode(value)}
                    >
                      <SelectTrigger id="consultationMode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={CONSULTATION_MODE.IN_PERSON}>
                          {t("schedule.consultationModes.in_person", "Presencial")}
                        </SelectItem>
                        <SelectItem value={CONSULTATION_MODE.VIRTUAL}>
                          {t("schedule.consultationModes.virtual", "Virtual")}
                        </SelectItem>
                        <SelectItem value={CONSULTATION_MODE.BOTH}>
                          {t("schedule.consultationModeBothOption", "Ambas")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {consultationMode === CONSULTATION_MODE.BOTH && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t(
                          "schedule.consultationModeHint",
                          "Si elegís \"Ambas\", el paciente o la secretaria eligen la modalidad al reservar."
                        )}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="slotDuration">
                      {t("app.scheduleConfig.slotDurationLabel", "Duración del turno (minutos)")}
                    </Label>
                    <Select
                      value={slotDuration.toString()}
                      onValueChange={(value) =>
                        setSlotDuration(Number(value))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">{t("app.scheduleConfig.duration15", "15 minutos")}</SelectItem>
                        <SelectItem value="30">{t("app.scheduleConfig.duration30", "30 minutos")}</SelectItem>
                        <SelectItem value="45">{t("app.scheduleConfig.duration45", "45 minutos")}</SelectItem>
                        <SelectItem value="60">{t("app.scheduleConfig.duration60", "1 hora")}</SelectItem>
                        <SelectItem value="90">{t("app.scheduleConfig.duration90", "1.5 horas")}</SelectItem>
                        <SelectItem value="120">{t("app.scheduleConfig.duration120", "2 horas")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <Button onClick={handleAddSchedule} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                {scheduleType === SCHEDULE_TYPE.BLOCKED
                  ? t("app.scheduleConfig.blockDates", "Bloquear fechas")
                  : t("app.scheduleConfig.addSchedule", "Agregar Horario")}
              </Button>
            </CardContent>
          </Card>

          <Card className="mb-20">
            <CardHeader>
              <CardTitle>{t("app.scheduleConfig.configuredTitle", "Horarios Configurados")}</CardTitle>
              <p className="text-sm text-gray-500 font-normal mt-1">
                {t("app.scheduleConfig.configuredHint", "Habitual por día; ocasionales y bloqueos por fechas.")}
              </p>
            </CardHeader>
            <CardContent>
              {!hasAnySchedules ? (
                <p className="text-gray-500 text-center py-8">
                  {t("app.scheduleConfig.noSchedulesYet", "No tienes horarios configurados aún")}
                </p>
              ) : (
                <div className="space-y-6">
                  {schedulesGroupedByDay.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-4 py-2 font-medium text-blue-900 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {t("app.scheduleConfig.habitualSchedules", "Horarios habituales")}
                      </div>
                      <div className="divide-y divide-blue-100">
                        {schedulesGroupedByDay.map(({ day, dayName, items }) => (
                          <div key={day}>
                            <div className="bg-blue-50/60 px-4 py-2 font-medium text-blue-900 flex items-center gap-2 border-b border-blue-100">
                              {dayName}
                              <span className="text-sm font-normal text-blue-700/80">
                                (
                                {items.length === 1
                                  ? t("app.scheduleConfig.scheduleCount", "{{count}} horario", {
                                      count: items.length,
                                    })
                                  : t("app.scheduleConfig.schedulesCount", "{{count}} horarios", {
                                      count: items.length,
                                    })}
                                )
                              </span>
                            </div>
                            <ul className="divide-y divide-gray-100">
                              {items.map((slot) => (
                                <li
                                  key={slot.id}
                                  className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-blue-50/30"
                                >
                                  <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
                                    {renderScheduleTypeBadge(slot)}
                                    {renderAppointmentBadge(slot)}
                                    <ConsultationModeBadge
                                      mode={getScheduleConsultationMode(slot)}
                                    />
                                    <span className="text-gray-500 text-sm">
                                      {formatClockTimeRange(
                                        slot.start_time,
                                        slot.end_time
                                      )}
                                      <span className="text-gray-400 ml-1">
                                        {t("app.scheduleConfig.slotsOfMinutes", "(turnos de {{minutes}} min)", {
                                          minutes: slot.slot_duration,
                                        })}
                                      </span>
                                    </span>
                                  </div>
                                  {deleteButton(slot)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {occasionalSchedules.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-amber-50 px-4 py-2 font-medium text-amber-900 flex items-center gap-2">
                        <CalendarDays className="w-4 h-4" />
                        {t("app.scheduleConfig.occasionalSchedules", "Horarios ocasionales")}
                      </div>
                      <ul className="divide-y divide-gray-100">
                        {occasionalSchedules.map((slot) => (
                          <li
                            key={slot.id}
                            className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50"
                          >
                            <div className="flex-1 min-w-0 flex flex-col gap-1">
                              <div className="flex flex-wrap items-center gap-2">
                                {renderScheduleTypeBadge(slot)}
                                {renderAppointmentBadge(slot)}
                                <ConsultationModeBadge
                                  mode={getScheduleConsultationMode(slot)}
                                />
                                <span className="text-gray-500 text-sm">
                                  {formatClockTimeRange(
                                    slot.start_time,
                                    slot.end_time
                                  )}
                                  <span className="text-gray-400 ml-1">
                                    (turnos de {slot.slot_duration} min)
                                  </span>
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatScheduleSpecificDates(slot)}
                              </span>
                            </div>
                            {deleteButton(slot)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {blockedSchedules.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-red-50 px-4 py-2 font-medium text-red-900 flex items-center gap-2">
                        <CalendarDays className="w-4 h-4" />
                        {t("app.scheduleConfig.blockedDays", "Días bloqueados")}
                      </div>
                      <ul className="divide-y divide-gray-100">
                        {blockedSchedules.map((slot) => (
                          <li
                            key={slot.id}
                            className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50"
                          >
                            <div className="flex-1 min-w-0 flex flex-col gap-1">
                              <div className="flex flex-wrap items-center gap-2">
                                {renderScheduleTypeBadge(slot)}
                                {slot.block_reason && (
                                  <span className="text-sm font-medium text-gray-800">
                                    {slot.block_reason}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatScheduleSpecificDates(slot)}
                              </span>
                            </div>
                            {deleteButton(slot)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
